import * as controllers from 'controllers'
import { NeedsClusterReinstalledDetailsDto } from 'dtos'
import { VehicleClusterReinstallPhotosProps } from './index'
import { useDialogState, useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import ImporterDoorLabelPhotoDialog from './ImporterDoorLabelPhotoDialog'
import ComboPhotoDialog from './ComboPhotoDialog'
import ClusterWithOdometerPhotoDialog from './ClusterWithOdometerPhotoDialog'
import OdometerAfterModificationPhotoDialog from './OdometerAfterModificationPhotoDialog'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import VehicleClusterReinstallStepper from './VehicleClusterReinstallStepper'
import VehiclePhotoCard from '../VehiclePhotoCard'
import { ArrowLeft, ArrowRight } from '@mui/icons-material'
import { ExplanationAccordion, GreenText } from 'components'

interface Step2ClusterReinstallPhotosProps extends VehicleClusterReinstallPhotosProps {
  onBack: () => void
  onNext: () => void
  vehicleOdometerReadingDetails: NeedsClusterReinstalledDetailsDto
  vehicleGuid?: string
}

export default function Step2ClusterReinstallPhotos({
  onBack,
  onNext,
  vehicleOdometerReadingDetails,
  vehicleGuid,
  importerDoorLabelPhoto,
  setImporterDoorLabelPhoto,
  comboPhoto,
  setComboPhoto,
  clusterWithOdometerPhoto,
  setClusterWithOdometerPhoto,
  odometerAfterModificationPhoto,
  setOdometerAfterModificationPhoto
}: Step2ClusterReinstallPhotosProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingImporterDoorLabel: false,
    isDeletingImporterDoorLabel: false,
    isUpdatingComboPhoto: false,
    isDeletingComboPhoto: false,
    isUpdatingClusterWithOdometerPhoto: false,
    isDeletingClusterWithOdometerPhoto: false,
    isUpdatingOdometerAfterModificationPhoto: false,
    isDeletingOdometerAfterModificationPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    importerDoorLabelPhotoDialogOpen: false,
    comboPhotoDialogOpen: false,
    clusterWithOdometerPhotoDialogOpen: false,
    odometerAfterModificationPhotoDialogOpen: false
  })

  const hasClusterWithOdometerPhoto = Boolean(clusterWithOdometerPhoto.documentGuid)
  const hasImporterDoorLabelPhoto = Boolean(importerDoorLabelPhoto.documentGuid)
  const hasComboPhoto = Boolean(comboPhoto.documentGuid)
  const hasOdometerAfterModificationPhoto = Boolean(odometerAfterModificationPhoto.documentGuid)

  const allPhotosUploaded =
    hasClusterWithOdometerPhoto && hasImporterDoorLabelPhoto && hasComboPhoto && hasOdometerAfterModificationPhoto

  return (
    <>
      <ClusterWithOdometerPhotoDialog
        initialValues={clusterWithOdometerPhoto}
        isLoading={loadingState.isDeletingClusterWithOdometerPhoto || loadingState.isUpdatingClusterWithOdometerPhoto}
        onClose={() => {
          setDialogState('clusterWithOdometerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingClusterWithOdometerPhoto', true)
          controllers
            .deleteNeedsClusterReinstalledClusterWithOdometerLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Speedometer After Modification Photo Deleted Successfully!', { variant: 'success' })
              setClusterWithOdometerPhoto(response.value)
              setDialogState('clusterWithOdometerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingClusterWithOdometerPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingClusterWithOdometerPhoto', true)
          controllers
            .updateNeedsClusterReinstalledClusterWithOdometerLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              setClusterWithOdometerPhoto(response.value)
              return controllers.getVehicleOdometerAfterModificationPhoto(vehicleGuid!)
            })
            .then(response => {
              enqueueSnackbar('Speedometer After Modification Saved Successfully!', { variant: 'success' })
              setOdometerAfterModificationPhoto(response.value)
              setDialogState('clusterWithOdometerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingClusterWithOdometerPhoto', false)
            })
        }}
        open={dialogState.clusterWithOdometerPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <OdometerAfterModificationPhotoDialog
        initialValues={odometerAfterModificationPhoto}
        isLoading={
          loadingState.isDeletingOdometerAfterModificationPhoto || loadingState.isUpdatingOdometerAfterModificationPhoto
        }
        onClose={() => {
          setDialogState('odometerAfterModificationPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingOdometerAfterModificationPhoto', true)
          controllers
            .deleteVehicleOdometerAfterModificationPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Odometer After Modification Photo Deleted Successfully!', { variant: 'success' })
              setOdometerAfterModificationPhoto(response.value)
              setDialogState('odometerAfterModificationPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingOdometerAfterModificationPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingOdometerAfterModificationPhoto', true)
          controllers
            .updateVehicleOdometerAfterModificationPhoto(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Odometer After Modification Photo Saved Successfully!', { variant: 'success' })
              setOdometerAfterModificationPhoto(response.value)
              setDialogState('odometerAfterModificationPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingOdometerAfterModificationPhoto', false)
            })
        }}
        open={dialogState.odometerAfterModificationPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <ImporterDoorLabelPhotoDialog
        initialValues={importerDoorLabelPhoto}
        isLoading={loadingState.isDeletingImporterDoorLabel || loadingState.isUpdatingImporterDoorLabel}
        onSave={values => {
          setLoadingState('isUpdatingImporterDoorLabel', true)
          controllers
            .updateNeedsClusterReinstalledImporterDoorLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Importer Door Label Saved Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              setDialogState('importerDoorLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingImporterDoorLabel', false)
            })
        }}
        onDelete={() => {
          setLoadingState('isDeletingImporterDoorLabel', true)
          controllers
            .deleteNeedsClusterReinstalledImporterDoorLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Importer Door Label Deleted Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              setDialogState('importerDoorLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingImporterDoorLabel', false)
            })
        }}
        open={dialogState.importerDoorLabelPhotoDialogOpen}
        onClose={() => {
          setDialogState('importerDoorLabelPhotoDialogOpen', false)
        }}
        vehicleGuid={vehicleGuid!}
      />

      <ComboPhotoDialog
        initialValues={comboPhoto}
        isLoading={loadingState.isDeletingComboPhoto || loadingState.isUpdatingComboPhoto}
        onClose={() => {
          setDialogState('comboPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingComboPhoto', true)
          controllers
            .deleteNeedsClusterReinstalledComboLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Combo Photo Deleted Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              setDialogState('comboPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingComboPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingComboPhoto', true)
          controllers
            .updateNeedsClusterReinstalledComboLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Combo Photo Saved Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              setDialogState('comboPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingComboPhoto', false)
            })
        }}
        open={dialogState.comboPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <VehicleClusterReinstallStepper activeStep={1} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='cluster-reinstall'
                complete={Boolean(clusterWithOdometerPhoto.documentGuid)}
                documentGuid={clusterWithOdometerPhoto.documentGuid}
                onClick={() => {
                  setDialogState('clusterWithOdometerPhotoDialogOpen', true)
                }}
                type='cluster-with-odometer'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='cluster-reinstall'
                complete={hasOdometerAfterModificationPhoto}
                fileName={odometerAfterModificationPhoto.displayFileName}
                label='Odometer After Modification'
                onClick={() => {
                  setDialogState('odometerAfterModificationPhotoDialogOpen', true)
                }}
                type='odometer-after-modification'
                vehicleGuid={vehicleGuid}
                documentGuid={odometerAfterModificationPhoto.documentGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='cluster-reinstall'
                complete={Boolean(importerDoorLabelPhoto.documentGuid)}
                documentGuid={importerDoorLabelPhoto.documentGuid}
                onClick={() => {
                  setDialogState('importerDoorLabelPhotoDialogOpen', true)
                }}
                type='importer-door-label'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='cluster-reinstall'
                complete={Boolean(comboPhoto.documentGuid)}
                documentGuid={comboPhoto.documentGuid}
                onClick={() => {
                  setDialogState('comboPhotoDialogOpen', true)
                }}
                type='combo'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} marginTop={2}>
              <Button
                color='primary'
                endIcon={<ArrowRight />}
                fullWidth
                onClick={onNext}
                type='submit'
                variant='contained'
                disabled={!allPhotosUploaded}
              >
                NEXT
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button color='primary' startIcon={<ArrowLeft />} fullWidth onClick={onBack} variant='outlined'>
                PREVIOUS
              </Button>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Vehicle Needs Cluster Reinstalled Steps:**

Vehicle Needs Cluster Reinstalled Steps: Displays the current step
of the vehicle in the Needs Cluster Reinstall process.

**Speedometer After Modification:**

Displays the Speedometer After Modification image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed
instead.

Clicking the Speedometer After Modification image will open the
Capture Speedometer After Modification Photo Dialog.

**Importer Door Label:**

Displays the Importer Door Label image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed
instead.

Clicking the Importer Door Label image will open the Capture
Importer Door Label Photo Dialog.

**Combo:**

Displays the Combo image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed
instead.

Clicking the Combo image will open the Capture Combo Photo
Dialog.

**NEXT Button:**

Clicking this button will:

   - Save the uploaded photo to the database.

   - Advance the user to the next step in the Needs Cluster Reinstalled
process.

This button remains disabled until a photo has been uploaded.

**PREVIOUS Button:**

Clicking this button will:

   - Discard any uploaded photo that has not been saved.

   - Return the user to the previous step in the process.

`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
